/**
 * Episode-aware date helpers for the Chief-complaints section.
 *
 * An episode in CC v2 is a contiguous run of status entries bounded by a
 * `vas === 0` marker (the equivalent of Dx's `ongoing === false`). Entries
 * with `event === "delete"` or `markedIncorrect: true` are excluded from
 * segmentation, matching the backend transformer's `countable` filter.
 *
 * Both helpers take a raw statusHistory array + a fallback value so they
 * work uniformly against a complaint record (`c.statusHistory`) and any
 * other context where only the audit array is in hand.
 */

const isCountable = (e) => !!e && e.event !== "delete" && !e.markedIncorrect;

/**
 * Start date of the MOST RECENT episode segment.
 *
 * For an ongoing complaint this is the current episode's start; for a
 * resolved complaint it is the start of the episode that just closed.
 * Algorithm: walk the countable audit backwards; skip a trailing resolve
 * (that closes the segment we want), find the previous resolve marker;
 * everything after it (or the whole chain if none) is the recent segment.
 * The FIRST entry of that segment carries the episode start `sinceWhen`.
 */
export const recentEpisodeSinceWhen = (statusHistory, fallback) => {
  const hist = Array.isArray(statusHistory) ? statusHistory : [];
  const countable = hist.filter(isCountable);
  if (countable.length === 0) return fallback;

  let i = countable.length - 1;
  if (countable[i].vas === 0) i -= 1;
  let boundary = -1;
  for (; i >= 0; i -= 1) {
    if (countable[i].vas === 0) {
      boundary = i;
      break;
    }
  }
  const start = countable[boundary + 1] || countable[0];
  return start.sinceWhen || fallback;
};

/**
 * Chronologically-latest `sinceWhen` across every countable status row —
 * regardless of event type (updates AND resolves both count). Used as the
 * MINIMUM for a NEW status update's date picker: a new status entry must
 * be dated on or after the most recent entry already on the audit so
 * clinicians can't record a status change that predates one already
 * logged (clinically nonsensical — if VAS went 7→5 on Oct 1, a
 * follow-up can't say 5→3 as of Sep 25).
 *
 * `fallback` is used when there's no countable entry with a sinceWhen —
 * mirrors recentEpisodeSinceWhen's contract.
 */
export const latestSinceWhen = (statusHistory, fallback) => {
  const hist = Array.isArray(statusHistory) ? statusHistory : [];
  let latest = null;
  for (const e of hist) {
    if (!isCountable(e)) continue;
    if (!e.sinceWhen) continue;
    if (!latest || new Date(e.sinceWhen).getTime() > new Date(latest).getTime()) {
      latest = e.sinceWhen;
    }
  }
  return latest || fallback;
};

/**
 * Patient-reported date from the audit entry that CLOSED the most recent
 * episode — the last countable status row with `vas === 0`. Returns
 * `fallback` if no countable resolve exists.
 */
export const recentResolveSinceWhen = (statusHistory, fallback) => {
  const hist = Array.isArray(statusHistory) ? statusHistory : [];
  for (let i = hist.length - 1; i >= 0; i -= 1) {
    const e = hist[i];
    if (!isCountable(e)) continue;
    if (e.vas === 0) return e.sinceWhen || fallback;
  }
  return fallback;
};
